import { getAnimationOptions } from "@client/lib/animation";
import { getAssetUrl } from "@client/lib/assets";
import { getVisibility } from "@client/lib/settings";
import { type CompositeSettings, MediaType } from "@client/types";
import { createSelector } from "reselect";
import { createShallowEqualSelector } from "../common";
import { screenSelector } from "../state";
import { type OutputParametricSelector } from "../types";
import { getCompositeSettingSelector, getSettingValueSelector } from "./base";
import styleSheetSelectors, { getColorSelector } from "./styleSheet";

export * from "./base";
export * from "./style";
export * from "./styleSheet";
export * from "./text";

export const getVisibilitySelector = (target: string, prefix?: string) => {
  const visibleSelector = getSettingValueSelector(target, "visible", prefix);

  return createSelector([visibleSelector], getVisibility);
};

export const getMediaUrlSelector = (valueSelector: OutputParametricSelector<any, any, any, any>, type: MediaType) =>
  createSelector([valueSelector], (value) => getAssetUrl(value, type));

export const getBackgroundSettingSelector = (target: string, prefix?: string) => {
  const backgroundSettingSelector = getCompositeSettingSelector<CompositeSettings.Background>(
    target,
    "background",
    prefix
  );
  const colorSelector = getColorSelector(target, "background.color", prefix);
  const overlayColorSelector = getColorSelector(target, "background.overlay.color", prefix);

  return createSelector(
    [backgroundSettingSelector, colorSelector, overlayColorSelector],
    (background, color, overlayColor): CompositeSettings.Background | null => {
      if (!background) return null;

      return {
        ...background,
        color,
        overlay: background.overlay
          ? {
              ...background.overlay,
              color: overlayColor
            }
          : background.overlay
      };
    }
  );
};

export const getBackgroundHoverSettingSelector = (target: string, prefix?: string) => {
  const backgroundSelector = getBackgroundSettingSelector(target, prefix);
  const hoverColorSelector = getColorSelector(target, "background.hoverColor", prefix, "primary");
  const overlayHoverColorSelector = getColorSelector(target, "background.overlay.hoverColor", prefix, "primary");

  return createSelector(
    [backgroundSelector, hoverColorSelector, overlayHoverColorSelector],
    (background, hoverColor, overlayHoverColor): CompositeSettings.BackgroundHover | null => {
      if (!background) return null;

      return {
        ...background,
        hoverColor,
        overlay: background.overlay
          ? {
              ...background.overlay,
              hoverColor: overlayHoverColor
            }
          : background.overlay
      };
    }
  );
};

export const getBorderSettingSelector = (target: string, prefix?: string) => {
  const settingSelector = getCompositeSettingSelector<CompositeSettings.Border>(target, "border", prefix);
  const colorSelector = getColorSelector(target, "border.color", prefix);

  return createSelector([settingSelector, colorSelector], (setting, color): CompositeSettings.Border | null =>
    setting
      ? {
          ...setting,
          color
        }
      : null
  );
};

export const getBorderHoverSettingSelector = (target: string, prefix?: string) => {
  const borderSelector = getBorderSettingSelector(target, prefix);
  const hoverColorSelector = getColorSelector(target, "border.hoverColor", prefix);

  return createSelector(
    [borderSelector, hoverColorSelector],
    (border, hoverColor): CompositeSettings.BorderHover | null =>
      border
        ? {
            ...border,
            hoverColor
          }
        : null
  );
};

export const getShadowSettingSelector = (target: string, prefix?: string) => {
  const settingSelector = getCompositeSettingSelector<CompositeSettings.Shadow>(target, "shadow", prefix);
  const colorSelector = getColorSelector(target, "shadow.color", prefix);

  return createSelector([settingSelector, colorSelector], (setting, color) =>
    setting
      ? {
          ...setting,
          color
        }
      : null
  );
};

export const getCornersRadiusSelector = (target: string, prefix?: string) => {
  const cornersSelector = getSettingValueSelector(target, "corners", prefix);
  const radiusSelector = getSettingValueSelector(target, "radius", prefix);

  return createSelector([cornersSelector, radiusSelector], (corners, radius) => {
    if (corners === "square") return "0px";
    if (corners === "round") return "2000px"; // set big value to cover any sizes
    return radius;
  });
};

export const makeAnimationOptionsSelector = (target: string, prefix?: string) => {
  const compositeSelector = getCompositeSettingSelector(target, "animation", prefix);
  return createShallowEqualSelector([compositeSelector, screenSelector], getAnimationOptions);
};

export const siteColorSelector = styleSheetSelectors.colors.primary;
